'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './JobCardStartup.module.css';

export interface JobCardStartupAttribute {
  id: number | string;
  label: string;
  emoji?: string;
}

export interface JobCardStartupProps extends React.HTMLAttributes<HTMLDivElement> {
  job: {
    id: number | string;
    title: string;
    company: string;
    companyLogo?: string;
    description?: string;
    attributes?: JobCardStartupAttribute[];
  };
  recruiterLogoUrl?: string;
  recruiterName?: string;
  className?: string;
}

export const JobCardStartup = React.forwardRef<HTMLDivElement, JobCardStartupProps>(
  ({ job, recruiterLogoUrl, recruiterName, className, ...props }, ref) => {
    const attributes = job.attributes ?? [];

    return (
      <div
        ref={ref}
        id={`card-${job.id}`}
        className={clsx(styles.root, className)}
        {...props}
      >
        <div className={styles.card}>
          <div className={styles.top}>
            <div className={styles.avatarWrapper}>
              {job.companyLogo ? (
                <img
                  src={job.companyLogo}
                  alt={job.company}
                  className={styles.avatarImage}
                />
              ) : recruiterLogoUrl ? (
                <img
                  src={recruiterLogoUrl}
                  alt={recruiterName || job.company}
                  className={styles.avatarImage}
                />
              ) : (
                <div className={styles.avatarFallback}>
                  {job.company
                    .split(' ')
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </div>
              )}
            </div>

            <div className={styles.headings}>
              <h2 className={styles.name}>{job.title}</h2>
              <h4 className={styles.title}>{job.company}</h4>
            </div>

            {recruiterLogoUrl && job.companyLogo && (
              <div className={styles.recruiterLogo}>
                <img src={recruiterLogoUrl} alt={recruiterName || ''} />
              </div>
            )}
          </div>

          {job.description && (
            <p className={styles.subtitle}>{job.description}</p>
          )}

          <div className={styles.attributes}>
            {attributes.map((item) => (
              <span className={styles.attribute} key={item.id}>
                {item.emoji && <span className={styles.emoji}>{item.emoji}</span>}
                <span
                  className={styles.attributeLabel}
                  style={{ marginLeft: item.emoji ? '6px' : '0' }}
                >
                  {item.label}
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }
);

JobCardStartup.displayName = 'JobCardStartup';

export default JobCardStartup;
